'use client'

import { Fragment } from 'react'
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react'
import { X } from 'lucide-react'
import { formatImageURL, formatPrice } from '@/utils/util'
import { useRewardVariantPicker, useVariantSelection } from '@/lib/commerce/hooks'
import { panelFocusRing } from '../elements/SidePanel'
import { OptionPicker } from '../product/VariantOptions'

const rowFocus = 'focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-foreground'
const buttonFocus = 'focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-foreground'
const sweepOnGroup = 'pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] bg-size-[0%_1px] group-hover:bg-size-[100%_1px] transition-[background-size] duration-500 ease-out motion-reduce:transition-none'

function RewardPrice({ price, original }: { price: number, original?: number }) {
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] tabular-nums">
      <span>{price === 0 ? 'Үнэгүй' : formatPrice(price)}</span>
      {!!original && price < original && <span className="text-xs line-through text-muted-foreground">{formatPrice(original)}</span>}
    </span>
  )
}

function RewardOptionChoice({ product, getPrice, onAdd }: { product: any, getPrice: (_variant: any) => number, onAdd: (_variantId: string) => void }) {
  const { selectedVariant, selectedOptions, selectOption, isSelectionPurchasable, isVariantAvailable } = useVariantSelection(product)
  const canAdd = isSelectionPurchasable && !!selectedVariant?.id && isVariantAvailable(selectedVariant)

  return (
    <div>
      <div className="flex items-center gap-4">
        <span className="w-14 aspect-3/4 shrink-0 overflow-hidden bg-muted">
          <img src={formatImageURL(selectedVariant?.images?.[0] || product.image || 'placeholder.png')} alt="" className="size-full object-cover" />
        </span>
        <div className="min-w-0">
          <div className="truncate text-[13px] text-muted-foreground">{isSelectionPurchasable ? selectedVariant?.name : 'Энэ хослол байхгүй'}</div>
          {selectedVariant && <div className="mt-1"><RewardPrice price={getPrice(selectedVariant)} original={selectedVariant.price} /></div>}
        </div>
      </div>
      <div className="mt-6">
        <OptionPicker product={product} selected={selectedOptions} onSelect={selectOption} />
      </div>
      <button type="button" disabled={!canAdd} onClick={() => { if (selectedVariant?.id) onAdd(selectedVariant.id) }} className={`mt-8 w-full h-12 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed ${buttonFocus}`}>
        {canAdd ? 'Сагсанд хийх' : isSelectionPurchasable ? 'Дууссан байна' : 'Энэ хослол байхгүй'}
      </button>
    </div>
  )
}

export default function RewardVariantModal() {
  const { isOpen, close, reward, variants, optionProduct, add, getPrice, isAvailable } = useRewardVariantPicker()

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-60" onClose={close}>
        <TransitionChild as={Fragment} enter="transition-opacity ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-foreground/30" />
        </TransitionChild>
        <div className="fixed inset-0 flex items-end sm:items-center justify-center sm:p-8">
          <TransitionChild as={Fragment} enter="transition ease-out duration-300 sm:duration-200 motion-reduce:transition-none" enterFrom="translate-y-full sm:translate-y-2 sm:opacity-0" enterTo="translate-y-0 sm:opacity-100" leave="transition ease-in duration-200 sm:duration-150 motion-reduce:transition-none" leaveFrom="translate-y-0 sm:opacity-100" leaveTo="translate-y-full sm:translate-y-2 sm:opacity-0">
            <DialogPanel className="w-full sm:max-w-lg flex flex-col max-h-[85dvh] sm:max-h-[calc(100dvh-4rem)] bg-background text-foreground">
              <div className="flex items-start justify-between gap-6 px-4 sm:px-8 pt-6 shrink-0">
                <div className="min-w-0">
                  <div className="text-[11px] tracking-[0.14em] uppercase text-muted-foreground">Бэлгийн бараа</div>
                  <DialogTitle className="mt-2 text-xl sm:text-2xl tracking-tight">Төрөл сонгох</DialogTitle>
                  <p className="mt-1 truncate text-[13px] text-muted-foreground">{reward?.name}</p>
                </div>
                <button type="button" onClick={close} aria-label="Хаах" className={`-mr-2 -mt-1 size-9 shrink-0 grid place-items-center transition-opacity duration-150 hover:opacity-60 ${panelFocusRing}`}>
                  <X className="size-5" strokeWidth={1.25} />
                </button>
              </div>

              {optionProduct ? (
                <div className="mt-6 flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-8 pb-6">
                  <RewardOptionChoice product={optionProduct} getPrice={getPrice} onAdd={add} />
                </div>
              ) : (
                <ul className="mt-6 flex-1 overflow-y-auto overscroll-contain px-4 sm:px-8 pb-6 divide-y divide-border">
                  {variants.map((variant: any) => {
                    const available = isAvailable(variant)
                    return (
                      <li key={variant.id}>
                        <button type="button" disabled={!available} onClick={() => add(variant.id)} className={`group w-full flex items-center gap-4 py-4 text-left disabled:cursor-not-allowed ${rowFocus}`}>
                          <span className="w-14 aspect-3/4 shrink-0 overflow-hidden bg-muted">
                            <img src={formatImageURL(variant.images?.[0] || reward?.image || 'placeholder.png')} alt="" className={`size-full object-cover ${available ? '' : 'opacity-50'}`} />
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className={`block truncate text-[13px] ${available ? 'text-foreground' : 'text-muted-foreground line-through'}`}>{variant.name}</span>
                            <span className="mt-1 block"><RewardPrice price={getPrice(variant)} original={variant.price} /></span>
                          </span>
                          <span className="shrink-0 text-xs">
                            {available ? <span className={sweepOnGroup}>Сагсанд хийх</span> : <span className="text-muted-foreground">Дууссан</span>}
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}
